import React, { useContext, useMemo, useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { trackQuickViewPageView } from '../actions';

const Context = React.createContext();
export const useQuickViewContext = () => {
  return useContext(Context);
};

/**
 * Provides modal orchestration state for the Quick View feature. This is the
 * public-facing context consumed by product cards, PDP components, and cart
 * flows to open the modal and react to its state.
 *
 * Pair with `QuickViewProductQueryContext` (internal) which handles the
 * GraphQL product query rendered inside the modal.
 *
 * @param {object} props
 * @param {React.ReactNode|function} props.children - Child content, or a
 *   render-prop function receiving the context value.
 * @param {object} [props.product={}] - Initial product to display. Overridden
 *   when `openQuickViewModal` is called with a new product.
 * @param {object} [props.cartItem={}] - Initial cart line item. Used when
 *   opening quick view from the cart.
 * @param {boolean} [props.showFullDetailsLink=true] - Whether to render the
 *   "View Full Details" link inside the modal.
 * @param {string} [props.pageIdentifier='/quick-view'] - Page name sent to
 *   analytics on modal open via `trackQuickViewPageView`.
 * @param {boolean} [props.useInternalResponseModal=false] - When true,
 *   `openResponseModal` shows the built-in add-to-bag success toast.
 * @param {boolean} [props.autoCloseInternalResponseModal=true] - When true,
 *   the response modal auto-dismisses after 3 seconds.
 *
 * @example
 * // Minimal usage — wrap a page or layout section
 * <QuickViewContext pageIdentifier="/shop/tops">
 *   <ProductGrid />
 *   <QuickViewModalWrapper />
 * </QuickViewContext>
 *
 * @example
 * // Consuming the context
 * const { openQuickViewModal, isModalOpen } = useQuickViewContext();
 */
export default function QuickViewContext({
  children,
  product: productFromProps = {},
  cartItem: cartItemFromProps = {},
  showFullDetailsLink = true,
  pageIdentifier = '/quick-view',
  useInternalResponseModal = false,
  autoCloseInternalResponseModal = true,
}) {
  const isQuickView = true;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [psrc, setPsrc] = useState();
  const [product, setProduct] = useState(productFromProps);
  const [cartItem, setCartItem] = useState(cartItemFromProps);
  const dispatch = useDispatch();
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isCartLineItemQuickView, setIsCartLineItemQuickView] = useState(false);
  const [productError, setProductError] = useState(false);

  const openQuickViewModal = useCallback(
    async (event, params) => {
      event.preventDefault();

      const { product, cartItem, psrc } = params;

      if (cartItem?.cartLineId) {
        setIsCartLineItemQuickView(true);
        setCartItem(cartItem);
        setProduct(product);
      } else {
        setIsCartLineItemQuickView(false);
        setCartItem(cartItem);
        setProduct(product);
      }

      if (psrc) {
        setPsrc(psrc);
      }

      setIsModalOpen(true);

      dispatch(
        trackQuickViewPageView({
          pageName: pageIdentifier,
          name: pageIdentifier,
        })
      );
    },
    [dispatch, pageIdentifier]
  );

  const closeQuickViewModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onProductError = useCallback(() => {
    closeQuickViewModal();
    setProductError(true);
  }, [closeQuickViewModal]);

  const clearProductError = useCallback(() => {
    setProductError(false);
  }, []);

  const closeResponseModal = useCallback(() => {
    setIsResponseModalOpen(false);
  }, []);

  const openResponseModal = useCallback(() => {
    if (useInternalResponseModal) {
      setIsResponseModalOpen(true);
      if (autoCloseInternalResponseModal) {
        setTimeout(() => {
          closeResponseModal();
        }, 3000);
      }
    }
  }, [
    useInternalResponseModal,
    autoCloseInternalResponseModal,
    closeResponseModal,
  ]);

  const context = useMemo(
    () => ({
      openQuickViewModal,
      closeQuickViewModal,
      isModalOpen,
      psrc,
      product,
      cartItem,
      showFullDetailsLink,
      isResponseModalOpen,
      openResponseModal,
      closeResponseModal,
      isQuickView,
      isCartLineItemQuickView,
      productError,
      onProductError,
      clearProductError,
    }),
    [
      openQuickViewModal,
      closeQuickViewModal,
      isModalOpen,
      psrc,
      product,
      cartItem,
      showFullDetailsLink,
      isResponseModalOpen,
      openResponseModal,
      closeResponseModal,
      isQuickView,
      isCartLineItemQuickView,
      productError,
      onProductError,
      clearProductError,
    ]
  );

  return (
    <Context.Provider value={context}>
      {typeof children === 'function' ? children(context) : children}
    </Context.Provider>
  );
}

QuickViewContext.propTypes = {
  autoCloseInternalResponseModal: PropTypes.bool,
  cartItem: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  pageIdentifier: PropTypes.string,
  product: PropTypes.object,
  showFullDetailsLink: PropTypes.bool,
  useInternalResponseModal: PropTypes.bool,
};
